import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mt-3 rounded-lg bg-gray-50 px-3 py-2.5" }
const _hoisted_2 = { class: "text-xs font-medium text-gray-400 uppercase tracking-wide" }
const _hoisted_3 = { class: "mt-1 flex items-center gap-1.5" }
const _hoisted_4 = { class: "text-base font-bold text-gray-900" }
const _hoisted_5 = { class: "grid grid-cols-2 gap-x-4" }
const _hoisted_6 = {
  key: 0,
  class: "mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
}
const _hoisted_7 = {
  key: 1,
  class: "mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
}
const _hoisted_8 = {
  key: 2,
  class: "mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
}
const _hoisted_9 = {
  key: 0,
  class: "flex items-center gap-4"
}
const _hoisted_10 = ["disabled"]
const _hoisted_11 = ["disabled", "title"]
const _hoisted_12 = { class: "flex flex-col-reverse gap-2 sm:flex-row sm:gap-x-2" }

import { computed, ref, watch } from 'vue';
import { useQueryClient, type QueryKey } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { format, parseISO } from 'date-fns';
import { useToast } from 'vue-toastification';
import { CornerDownRight } from 'lucide-vue-next';
import CustomButton from '@/components/ui/CustomButton.vue';
import CustomInput from '@/components/ui/CustomInput.vue';
import CustomTextArea from '@/components/ui/CustomTextArea.vue';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import DatePicker from '@/components/ui/DatePicker.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys as productionGroupQueryKeys, useProductionGroupTimeline, useBackfillPigTallySeries, type ProductionGroupTimelineRequest } from '@/plugins/store/actions/queries/production-groups';
import { IconSource, type ProductionGroup } from '@/types';
import SectionCamerasReadout from './SectionCamerasReadout.vue';
import ProductionGroupTimelineChart from './ProductionGroupTimelineChart.vue';
import {
    findProductionGroupOverlap,
    findSuggestedProductionGroupStart,
    getProductionGroupTimelineRange,
    isProductionGroupRangeTooShort,
    pigTallyAtDate,
    productionGroupIntersectsTimeline,
    type ProductionGroupTimelineRange,
} from './productionGroupTimeline';

// Shared create/adjust/close modal for a single Section's Production Group - opened from Active
// groups (one row per physical Section, already resolved) and from Groups overview (resolved from
// the clicked group, or from a Barn + a chosen Section for create). Mode is derived from
// `target.productionGroupId`: null → create, set → edit. Carries only the date fields the PRD scopes
// here (name / animal group / pig count are out of scope for the field set, though animal group is
// still derived and sent server-side); Groups overview needs actual delete on top (see `allowDelete`),
// which Active groups doesn't expose.
const NOTE_MAX_LENGTH = 1000;

// The minimum a caller must resolve before opening this modal. Deliberately narrower than Active
// groups' own `SectionRow` (which structurally satisfies this and can be passed straight through) so
// Groups overview isn't forced to build a full SectionRow just to open it.
export type ManageProductionGroupTarget = {
    id: number;
    sectionName: string;
    farmName: string;
    barnName: string;
    barnId: number;
    productionGroupId: number | null;
};

interface ManageState {
    name: string;
    note: string;
    startDate: string;
    endDate: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ManageProductionGroupModal',
  props: /*@__PURE__*/_mergeModels({
    target: {},
    productionGroup: {},
    allowDelete: { type: Boolean },
    extraInvalidateQueryKeyRoots: {}
  }, {
    "modelValue": { type: Boolean },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["delete"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const showModal = _useModel<boolean>(__props, 'modelValue');

const queryClient = useQueryClient();
const toast = useToast();

const isLoading = ref(false);
// True while a Forced Recompute call is in flight (edit mode only, triggered by its own button).
const isRecalculating = ref(false);

const isEditMode = computed(() => props.target?.productionGroupId != null);
const sectionName = computed(() => props.target?.sectionName ?? '');
const farmName = computed(() => props.target?.farmName ?? '');
const barnName = computed(() => props.target?.barnName ?? '');
const todayIso = computed(() => format(new Date(), 'yyyy-MM-dd'));

function getInitialState(): ManageState {
    return {
        name: '',
        note: '',
        startDate: '',
        endDate: '',
    };
}

const state = ref<ManageState>(getInitialState());

// Snapshot of state as seeded when the modal opened — Save stays disabled until something in the
// form actually differs, so it can't be used as a no-op stand-in for the Recompute button.
const originalState = ref<ManageState>(getInitialState());
const initialCreateStartDate = ref<string | null>(null);
const hasAppliedSuggestedStart = ref(false);

// Centered on the form's own start date (3 months back, 4 forward) once one is picked, so the chart
// follows whichever Production Group is actually being created/edited instead of a fixed "last six
// months" window that could leave an older one - or the one currently being edited - off-screen
// entirely. Falls back to today while the field is still blank (fresh create mode). This is the
// chart's own x-axis range - it's allowed to reach past today so a future-dated plan still draws.
const timelineRange = computed<ProductionGroupTimelineRange>(() => {
    const start = toDateOnly(state.value.startDate);
    return getProductionGroupTimelineRange(start ? parseISO(start) : new Date());
});

// What's actually queried from the backend: both the tally and the Production Group reads reject a
// future `to` (there's nothing to return past today), so this clamps the chart's own forward-looking
// range down to today for the fetch while leaving `timelineRange` itself - the chart's x-axis - free
// to extend further, drawing as empty space the same way the Active groups tally chart already does.
const queryRange = computed<ProductionGroupTimelineRange>(() => ({
    from: timelineRange.value.from,
    to: timelineRange.value.to < todayIso.value ? timelineRange.value.to : todayIso.value,
}));

const timelineRequest = computed<ProductionGroupTimelineRequest | null>(() =>
    showModal.value && props.target
        ? {
              sectionId: props.target.id,
              barnId: props.target.barnId,
              from: queryRange.value.from,
              to: queryRange.value.to,
          }
        : null
);
// useBackfillPigTallySeries (not active-groups' usePigTallySeries) deliberately - that one's backend
// caps lookback at 6 months before today for its own KPI-detail caller, so a group whose own window
// is older than that (e.g. editing one from last year) would 400 the whole modal. The backfill-business
// endpoint has no such floor and already clamps `to` to today itself, matching queryRange's own clamp.
const pigTallyRange = computed<{ from: string; to: string } | null>(() => (timelineRequest.value ? { from: queryRange.value.from, to: queryRange.value.to } : null));
const sectionId = computed(() => props.target?.id ?? null);
const { data: pigTallyData, isLoading: isPigTallyLoading, isError: isPigTallyError, refetch: refetchPigTally } = useBackfillPigTallySeries(sectionId, pigTallyRange);
const {
    data: productionGroupTimelineData,
    isLoading: isProductionGroupTimelineLoading,
    isError: isProductionGroupTimelineError,
    refetch: refetchProductionGroupTimeline,
} = useProductionGroupTimeline(timelineRequest);
const pigTally = computed(() => pigTallyData.value ?? []);
const allTimelineProductionGroups = computed(() => productionGroupTimelineData.value ?? []);
const timelineProductionGroups = computed(() => allTimelineProductionGroups.value.filter((productionGroup) => productionGroupIntersectsTimeline(productionGroup, timelineRange.value)));
const isTimelineLoading = computed(() => isPigTallyLoading.value || isProductionGroupTimelineLoading.value);
const hasTimelineError = computed(() => isPigTallyError.value || isProductionGroupTimelineError.value);
const rawSuggestedStart = computed(() => findSuggestedProductionGroupStart(pigTally.value));
const suggestedStartConflicts = computed(
    () =>
        rawSuggestedStart.value !== null &&
        findProductionGroupOverlap({ start: rawSuggestedStart.value, end: null, productionGroups: allTimelineProductionGroups.value, today: todayIso.value, ignoredProductionGroupId: null }) !== null
);
const suggestedStart = computed(() => (suggestedStartConflicts.value ? null : rawSuggestedStart.value));

// True when the end date is set and falls before the start date.
const dateRangeInvalid = computed(() => {
    const start = toDateOnly(state.value.startDate);
    return start !== null && isProductionGroupRangeTooShort(start, toDateOnly(state.value.endDate));
});

// Compares normalized values, not raw ISO strings — the DatePicker round-trips timestamps that
// differ from the seeded value even when the calendar day hasn't changed.
function toDateOnly(iso: string): string | null {
    return iso ? format(parseISO(iso), 'yyyy-MM-dd') : null;
}

// The inverse: DatePicker's v-model (and its display formatting) is a timestamp, not a bare date.
// A date-only "yyyy-MM-dd" fed straight into it is parsed as UTC midnight and can render as the
// wrong calendar day in negative-offset timezones, so every date-only source (tally dates, the
// suggested start) must go through here before reaching `state.startDate`.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}

// An existing Production Group may be moved earlier, but never start after its current end date.
// Create mode remains capped at today, because its end is still optional and user-entered.
const startDateMax = computed(() => {
    const end = isEditMode.value ? toDateOnly(state.value.endDate) : null;
    return end && end < todayIso.value ? end : todayIso.value;
});

// Guard the picker constraint at submit time too, because a model can be set programmatically or
// by typed input. Production Groups cannot start in the future.
const startDateInFuture = computed(() => {
    const startDate = toDateOnly(state.value.startDate);
    return startDate != null && startDate > todayIso.value;
});

const overlappingProductionGroup = computed(() => {
    const start = toDateOnly(state.value.startDate);
    if (!start || dateRangeInvalid.value) return null;

    return findProductionGroupOverlap({
        start,
        end: toDateOnly(state.value.endDate),
        productionGroups: allTimelineProductionGroups.value,
        today: todayIso.value,
        ignoredProductionGroupId: isEditMode.value ? props.target?.productionGroupId ?? null : null,
    });
});

// The tally warning is intentionally advisory: a farmer can knowingly begin analysis mid-batch.
// The suggested start is the first non-zero day after an empty Section, so it is explicitly valid.
const startHasPigs = computed(() => {
    const start = toDateOnly(state.value.startDate);
    return start !== null && start !== suggestedStart.value && (pigTallyAtDate(pigTally.value, start) ?? 0) > 0;
});

// Name and start date required; start cannot be future-dated, end date (when set) must not precede
// start date, and the overlap/suggestion data (pig tally + Production Group timeline, both folded into
// `isTimelineLoading`/`hasTimelineError`) must have loaded cleanly — overlap prevention and the
// non-zero-start warning are both meaningless against stale or missing data.
const isFormValid = computed(
    () =>
        state.value.name.trim().length > 0 &&
        state.value.startDate.length > 0 &&
        !startDateInFuture.value &&
        !dateRangeInvalid.value &&
        !isTimelineLoading.value &&
        !hasTimelineError.value &&
        !overlappingProductionGroup.value
);

const isDirty = computed(
    () =>
        state.value.name.trim() !== originalState.value.name.trim() ||
        state.value.note.trim() !== originalState.value.note.trim() ||
        toDateOnly(state.value.startDate) !== toDateOnly(originalState.value.startDate) ||
        toDateOnly(state.value.endDate) !== toDateOnly(originalState.value.endDate)
);

// Refreshes the Production Group list every caller shares, plus whatever else the calling page
// cached locally for this Section (its `extraInvalidateQueryKeyRoots` - e.g. Active groups' live
// KPIs and series charts).
function refreshSectionData() {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: productionGroupQueryKeys.getProductionGroupsRoot }),
        ...(props.extraInvalidateQueryKeyRoots ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    ]);
}

async function handleSubmit() {
    if (isLoading.value || isRecalculating.value || !props.target || !isFormValid.value || (isEditMode.value && !isDirty.value)) {
        return;
    }

    isLoading.value = true;
    try {
        const start = format(parseISO(state.value.startDate), 'yyyy-MM-dd');
        const end = state.value.endDate ? format(parseISO(state.value.endDate), 'yyyy-MM-dd') : null;
        // The column is nullable and an empty note is the absence of one, so blank text is sent as
        // null rather than storing an empty string alongside it.
        const note = state.value.note.trim() || null;

        if (isEditMode.value) {
            await store.dispatch(ActionType.EditProductionGroups, {
                options: { skipHandleError: true },
                form: {
                    production_groups: [
                        {
                            id: props.target.productionGroupId!,
                            name: state.value.name.trim(),
                            note,
                            start,
                            end,
                            is_favorite: props.productionGroup?.is_favorite ?? false,
                        },
                    ],
                },
            });

            await forceRecompute(props.target.productionGroupId!);
        } else {
            // A new Production Group has no derived figures until Nexus has rebuilt its Production
            // Cycle. Wait for that targeted recompute before refreshing the overview, so its
            // first read cannot show a partly-created period with stale/null calculations.
            const createdProductionGroup: ProductionGroup = await store.dispatch(ActionType.CreateProductionGroup, {
                options: { skipHandleError: true },
                form: {
                    location_id: props.target.id,
                    name: state.value.name.trim(),
                    note,
                    start,
                    end,
                    is_favorite: false,
                },
            });

            await forceRecompute(createdProductionGroup.id);
        }

        await refreshSectionData();

        showModal.value = false;
        toast.success(
            isEditMode.value
                ? i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.edited')
                : i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.created')
        );
    } catch (err) {
        const errorMessage =
            (err as any)?.data?.conflicts?.[0]?.conflict_message || (err as any)?.data?.message || i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.couldNotSave');
        toast.error(errorMessage);
    } finally {
        isLoading.value = false;
    }
}

// Forced Recompute: on-demand, synchronous, single-cycle recompute of DLM + Water Norm figures.
// Independent of Save — runs against whatever is currently persisted for this Production Group.
async function handleForceRecompute() {
    if (isRecalculating.value || isLoading.value || props.target?.productionGroupId == null) {
        return;
    }

    isRecalculating.value = true;
    try {
        await forceRecompute(props.target.productionGroupId);
        await refreshSectionData();
        toast.success(i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.successMessages.recomputed'));
    } catch {
        toast.error(i18n.global.t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.recomputeFailed'));
    } finally {
        isRecalculating.value = false;
    }
}

function forceRecompute(productionGroupId: number) {
    return store.dispatch(ActionType.ForceRecomputeProductionGroup, {
        options: { skipHandleError: true },
        form: { productionGroupId },
    });
}

function handleUseSuggestedStart(start: string) {
    state.value.startDate = toPickerValue(start);
}

function refreshTimeline() {
    return Promise.all([refetchPigTally(), refetchProductionGroupTimeline()]);
}

// Create mode uses the latest observed empty-to-occupied transition as a helpful default. It runs
// only once and only while the untouched blank value remains, so a late query result cannot replace
// a date the farmer already entered. Edit mode always remains an explicit helper action instead.
function applySuggestedStartIfAppropriate(start: string | null) {
    if (!showModal.value || !start || isEditMode.value || hasAppliedSuggestedStart.value || toDateOnly(state.value.startDate) !== initialCreateStartDate.value) return;

    state.value.startDate = toPickerValue(start);
    hasAppliedSuggestedStart.value = true;
}

// Seed the form from the active Production Group (edit) or defaults (create) each time the modal opens.
watch(showModal, (value) => {
    if (value) {
        hasAppliedSuggestedStart.value = false;
        state.value = isEditMode.value
            ? {
                  name: props.productionGroup?.name ?? '',
                  note: props.productionGroup?.note ?? '',
                  startDate: props.productionGroup?.start ? new Date(props.productionGroup.start).toISOString() : new Date().toISOString(),
                  endDate: props.productionGroup?.end ? new Date(props.productionGroup.end).toISOString() : '',
              }
            : getInitialState();
        originalState.value = { ...state.value };
        initialCreateStartDate.value = toDateOnly(state.value.startDate);
        applySuggestedStartIfAppropriate(suggestedStart.value);
    }
});

watch(suggestedStart, applySuggestedStartIfAppropriate);

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createBlock(_unref(Dialog), {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showModal).value = $event)),
    disabled: isLoading.value || isRecalculating.value,
    class: "sm:max-w-[720px]",
    "reserve-scrollbar-gutter": ""
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(DialogHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(DialogTitle), null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(isEditMode.value ? _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.editTitle') : _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.createTitle')), 1)
            ]),
            _: 1
          }),
          _createVNode(_unref(VisuallyHidden), null, {
            default: _withCtx(() => [
              _createVNode(_unref(DialogDescription), { class: "pt-4" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.description')), 1)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("p", _hoisted_2, _toDisplayString(farmName.value) + " · " + _toDisplayString(barnName.value), 1),
        _createElementVNode("p", _hoisted_3, [
          _createVNode(_unref(CornerDownRight), {
            class: "h-3.5 w-3.5 shrink-0 text-gray-400",
            "aria-hidden": "true"
          }),
          _createElementVNode("span", _hoisted_4, _toDisplayString(sectionName.value), 1)
        ])
      ]),
      _createElementVNode("form", {
        class: "mt-4 space-y-3",
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, [
        _createVNode(CustomInput, {
          value: state.value.name,
          "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((state.value.name) = $event)),
          required: "",
          "input-type": "text",
          rules: [],
          label: _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.name')
        }, null, 8, ["value", "label"]),
        _createVNode(CustomTextArea, {
          value: state.value.note,
          "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((state.value.note) = $event)),
          required: false,
          rows: 2,
          maxlength: NOTE_MAX_LENGTH,
          rules: [],
          label: _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.note')
        }, null, 8, ["value", "label"]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(DatePicker, {
            value: state.value.startDate,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((state.value.startDate) = $event)),
            required: "",
            max: startDateMax.value,
            label: _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.startDate')
          }, null, 8, ["value", "max", "label"]),
          _createVNode(DatePicker, {
            value: state.value.endDate,
            "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((state.value.endDate) = $event)),
            resetable: "",
            min: state.value.startDate,
            max: todayIso.value,
            label: _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.fields.endDate')
          }, null, 8, ["value", "min", "max", "label"])
        ])
      ], 32),
      _createVNode(SectionCamerasReadout, {
        class: "mt-3",
        "section-id": sectionId.value,
        date: toDateOnly(state.value.startDate)
      }, null, 8, ["section-id", "date"]),
      _createVNode(ProductionGroupTimelineChart, {
        class: "mt-4",
        tally: pigTally.value,
        "production-groups": timelineProductionGroups.value,
        range: timelineRange.value,
        "form-start": toDateOnly(state.value.startDate) ?? '',
        "form-end": toDateOnly(state.value.endDate) ?? '',
        "edited-production-group-id": isEditMode.value ? _ctx.target?.productionGroupId ?? null : null,
        "suggested-start": suggestedStart.value,
        "suggestion-conflicts": suggestedStartConflicts.value,
        "is-loading": isTimelineLoading.value,
        "has-error": hasTimelineError.value,
        onUseSuggestedStart: handleUseSuggestedStart,
        onRetry: refreshTimeline
      }, null, 8, ["tally", "production-groups", "range", "form-start", "form-end", "edited-production-group-id", "suggested-start", "suggestion-conflicts", "is-loading", "has-error"]),
      (dateRangeInvalid.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.endBeforeStart')), 1))
        : (overlappingProductionGroup.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.errors.overlapsExisting', { name: overlappingProductionGroup.value.name })), 1))
          : _createCommentVNode("", true),
      (startHasPigs.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.warnings.nonZeroStart')), 1))
        : _createCommentVNode("", true),
      _createVNode(_unref(DialogFooter), {
        class: _normalizeClass(["mt-2", isEditMode.value ? 'sm:justify-between' : ''])
      }, {
        default: _withCtx(() => [
          (isEditMode.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                (_ctx.allowDelete)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      type: "button",
                      class: "text-sm font-medium text-red-600 transition-colors hover:text-red-700 disabled:cursor-default disabled:opacity-50",
                      disabled: isLoading.value || isRecalculating.value,
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.target?.productionGroupId != null && emit('delete', _ctx.target.productionGroupId)))
                    }, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.delete')), 9, _hoisted_10))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  class: "inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800 disabled:cursor-default disabled:opacity-50",
                  disabled: isLoading.value || isRecalculating.value,
                  title: _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.recompute'),
                  onClick: handleForceRecompute
                }, [
                  _createVNode(_component_Icon, {
                    src: _unref(IconSource).RefreshActivities,
                    class: _normalizeClass({ 'animate-spin': isRecalculating.value }),
                    style: {"width":"15px","height":"15px"}
                  }, null, 8, ["src", "class"]),
                  _createElementVNode("span", null, _toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.recompute')), 1)
                ], 8, _hoisted_11)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_unref(DialogClose), { "as-child": "" }, {
              default: _withCtx(() => [
                _createVNode(CustomButton, {
                  disabled: isLoading.value || isRecalculating.value,
                  "color-preset": "white",
                  class: "w-auto min-w-28"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.cancel')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled"])
              ]),
              _: 1
            }),
            _createVNode(CustomButton, {
              loading: isLoading.value,
              disabled: !isFormValid.value || isRecalculating.value || (isEditMode.value && !isDirty.value),
              class: "w-auto min-w-28",
              onClick: handleSubmit
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(isEditMode.value ? _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.save') : _ctx.$t('views.monitoring.productionGroups.modals.manageProductionGroup.buttons.create')), 1)
              ]),
              _: 1
            }, 8, ["loading", "disabled"])
          ])
        ]),
        _: 1
      }, 8, ["class"])
    ]),
    _: 1
  }, 8, ["modelValue", "disabled"]))
}
}

})